<template>

  <!-- Collapsible group content: forms, subgroups -->
  <div v-show="contentDisplayed" class="social-footer" :id="'form-group-' + getGroupKey(parentGroup)">

    <template v-if="contentLoaded">
      <!-- Message if no forms/subgroups loaded -->
      <p v-if="!(groups ? groups.length : false) && !(forms ? forms.length : false)" class="form-control-plaintext ml-3" :style="nestingIndentStyleMessage">
        {{ noFormsMessage }}
      </p>

      <!-- Group items -->
      <template v-else>

        <!-- Subgroups -->
        <form-group v-for="group in groups"
          :key="getGroupKey(group)"
          ref="groups"
          :group="group"
          :tab="tab"
          :show-all-groups-mode="showAllGroupsMode"
          :manage-groups-allowed="manageGroupsAllowed"
          :no-forms-message="noFormsMessage"
          :nesting-level="nestingLevel + 1"
          :view-mode="viewMode"
          @updateIsEmpty="updateSubgroupsIsEmptyStatus()"
          @onContentIsLoading="updateSubgroupsContentIsLoading()"
          @groupSelectionChanged="subgroupSelectionChanged(getGroupKey(group), $event)"
          @selectedFormsUpdated="updateSelectedFormsInSubgroup(getGroupKey(group), $event)"
          @selectedGroupsUpdated="updateSelectedGroupsInSubgroup(getGroupKey(group), $event)"
        >
        </form-group>

        <!-- Forms -->
        <ul class="list-group">
          <li v-for="form in forms" class="list-group-item">
            <div class="float-left mr-2" :style="nestingIndentStyleForm">
              <input :id="'form-' + getFormKey(form)" class="form-id custom-checkbox" v-model="selectedFormIds" :value="getFormKey(form)" type="checkbox"/>
              <label :for="'form-' + getFormKey(form)"></label>
            </div>

            <span class="form-date">{{ form.created_at }}</span>

            <a :href="form.download_link" target="_blank">
              <s v-if="!form.status" class="text-muted">{{ form.name }}</s>
              <span v-else>{{ form.name }}</span>
            </a>
          </li>
        </ul>

      </template>
    </template>

  </div>
  <!-- // End of collapsible group content: forms, subgroups -->

</template>

<script>
/**
 * This widget is a part of FormExplorer. It shows a form group items: forms, subgroups.
 */
export default {

  emits: [
    'selectedFormsUpdated',
    'selectedGroupsUpdated',
    'onContentLoad',
    'onContentSelect',
    'onContentIsLoading',
    'updateIsEmpty'
  ],

  /**
   * Input properties.
   *
   * Usage examples:
   *
   <form-group-content
     :parent-group="group"
     :tab="tab"
     :show-all-groups-mode="showAllGroupsMode"
     :manage-groups-allowed="manageGroupsAllowed"
     :no-forms-message="noFormsMessage"
     :do-display-content="doDisplayContent"
     :do-load-content="doLoadContent"
     @onContentLoad="contentLoaded = $event"
   >
   */
  props: {

    // Parent group object.
    parentGroup: {
      type: Object,
      default: () => {return {};}
    },

    // Current tab key.
    tab: {
      type: String,
      default: 'active'
    },

    // Show all groups mode, including inactive.
    showAllGroupsMode: {
      type: Boolean,
      default: false
    },

    // Indicates if user can manage the groups.
    manageGroupsAllowed: {
      type: Boolean,
      default: false
    },

    // The message to be shown if no data loaded.
    noFormsMessage: {
      type: String,
      default: 'No forms'
    },

    // Indicates the component should load the content.
    doLoadContent: {
      type: Boolean,
      default: false
    },

    // Indicates the component should display the content.
    doDisplayContent: {
      type: Boolean,
      default: false
    },

    // Indicates the component should select all items.
    doSelectContent: {
      type: Number,
      default: -1
    },

    // Current group nesting level.
    nestingLevel: {
      type: Number,
      default: 0
    },

    // Show widget in "view mode" (no "edit" buttons, etc).
    viewMode: {
      type: Boolean,
      default: false
    },

  },

  /**
   * Variables (models).
   */
  data() {
    return {
      // Selected form ids.
      selectedFormIds: [],

      // Selected subgroup keys.
      selectedGroupKeys: [],

      // Subgroups.
      groups: [],

      // Forms.
      forms: [],

      // Is content loaded.
      contentLoaded: false,

      // Is content displayed.
      contentDisplayed: false,

      // Is content selected.
      contentSelected: false,

      // Selected form ids by groups.
      selectedFormIdsByGroups: [],

      // Selected subgroups by groups.
      selectedGroupsByGroups: [],

      // Is content loading now.
      contentIsLoading: false,

      // Is content loading in subgroups now.
      subgroupsContentIsLoading: false,

      // Is group empty.
      isEmpty: false,

      // How many not empty subgroups are in this group.
      notEmptySubgroupsCount: -1,
    }
  },

  /**
   * Functions (methods).
   */
  methods: {

    /**
     * Loads group content.
     */
    load(onLoad) {
      this.contentIsLoading = true;
      let path = '/forms/lookup/' + this.tab + '/' + this.parentGroup.id + '/items';
      if (this.parentGroup.zip_item) path += '/' + this.parentGroup.zip_item.index;
      axios.get(path)
        .then(response => {
          this.forms = response.data.forms;
          this.groups = response.data.groups;
          this.updateSubgroupsIsEmptyStatus();

          this.selectedFormIdsByGroups = [];
          this.groups.forEach(group => {this.selectedFormIdsByGroups.push({groupKey: this.getGroupKey(group), selectedFormIds: []});});

          this.selectedGroupKeysByGroups = [];
          this.groups.forEach(group => {this.selectedGroupKeysByGroups.push({groupKey: this.getGroupKey(group), selectedGroupKeys: []});});

          this.contentLoaded = true;
          if (onLoad) onLoad();
        })
        .catch(error => {
          this.contentLoaded = false;
        })
        .then(() => {
          this.contentIsLoading = false;
        });
    },

    /**
     * Selects group content.
     */
    select(doSelect) {
      // Select all subgroups.
      if (this.groups.length) {
        if (doSelect > 0) {
          this.$nextTick(() => {
            this.$refs.groups.forEach(groupComponent => groupComponent.select(true));
          });
        }
        if (doSelect < 0) {
          this.$nextTick(() => {
            this.$refs.groups.forEach(groupComponent => groupComponent.select(false));
          });
        }
      }
      // Select all forms.
      if (this.forms.length) {
        if (doSelect > 0) {
          this.selectedFormIds = this.forms.map(form => this.getFormKey(form));
        }
        if (doSelect < 0) {
          this.selectedFormIds = [];
        }
      }
    },

    /**
     * Selects/unselects a form.
     */
    toggleSelectForm(formId) {
      if (this.isFormSelected(formId)) {
        this.selectedFormIds.splice(this.selectedFormIds.indexOf(formId), 1);
      } else {
        this.selectedFormIds.push(formId);
      }
    },

    /**
     * Indicates if the specified form is selected.
     */
    isFormSelected(formId) {
      return this.selectedFormIds.includes(formId);
    },

    /**
     * Event fires when one of subgroup selection changed.
     */
    subgroupSelectionChanged(groupKey, isSelected) {
      if (isSelected) {
        this.selectedGroupKeys.push(groupKey);
      } else {
        this.selectedGroupKeys.splice(this.selectedGroupKeys.indexOf(groupKey), 1);
      }
    },

    /**
     * Indicates if the specified form is selected.
     */
    isGroupSelected(groupKey) {
      return this.selectedGroupKeys.includes(groupKey);
    },

    /**
     * Update selected form ids from form-group sub-component.
     */
    updateSelectedFormsInSubgroup(groupKey, newFormIds) {
      let index = this.selectedFormIdsByGroups.findIndex(groupInfo => groupInfo.groupKey === groupKey);
      if (index !== -1) this.selectedFormIdsByGroups[index].selectedFormIds = newFormIds;
      this.updateAllSelectedFormIds();
    },

    /**
     * Update selected form ids from form-group sub-component.
     */
    updateSelectedGroupsInSubgroup(groupKey, newGroupKeys) {
      let index = this.selectedGroupKeysByGroups.findIndex(groupInfo => groupInfo.groupKey === groupKey);
      if (index !== -1) this.selectedGroupKeysByGroups[index].selectedGroupKeys = newGroupKeys;
      this.updateAllSelectedGroupKeys();
    },

    /**
     * Update current group form ids and all subgroups form ids and fire "selectedFormsUpdated" event to parent.
     */
    updateAllSelectedFormIds() {
      let selectedIds = this.selectedFormIds;

      // Merge selected form ids from all groups.
      this.selectedFormIdsByGroups.forEach(groupInfo => {selectedIds = [...selectedIds, ...groupInfo.selectedFormIds]});
      // Remove duplicates.
      [...new Set(selectedIds)];

      this.$emit('selectedFormsUpdated', selectedIds);
    },

    /**
     * Update current group subgroups and all subgroups groups and fire "selectedGroupsUpdated" event to parent.
     */
    updateAllSelectedGroupKeys() {
      let selectedKeys = this.selectedGroupKeys;

      // Merge selected subgroups from all groups.
      this.selectedGroupKeysByGroups.forEach(groupInfo => {selectedKeys = [...selectedKeys, ...groupInfo.selectedGroupKeys]});
      // Remove duplicates.
      [...new Set(selectedKeys)];

      this.$emit('selectedGroupsUpdated', selectedKeys);
    },

    /**
     * Generate a group key.
     */
    getGroupKey(group) {
      return group.id + (group.zip_item ? '_' + group.zip_item.index : '');
    },

    /**
     * Generate a form key.
     */
    getFormKey(form) {
      return form.id + (form.zip_item ? '_' + form.zip_item.index : '');
    },

    /**
     * Indicates if any subgroup content is loading now.
     */
    updateSubgroupsContentIsLoading() {
      let isLoading = false;
      if (this.$refs.groups) {
        this.$refs.groups.forEach(groupComponent => {
          if (groupComponent.isContentLoading) isLoading = true;
        });
      }
      this.subgroupsContentIsLoading = isLoading;
    },

    /**
     * Updates empty subgroups list.
     */
    updateSubgroupsIsEmptyStatus() {
      this.notEmptySubgroupsCount = this.groups.length;
      this.$nextTick(() => {
        if (this.$refs.groups) {
          this.notEmptySubgroupsCount = this.$refs.groups.filter(groupComponent => {
            return !groupComponent.isEmpty;
          }).length;
        }
      });
    },

  },

  /**
   * Computed values
   */
  computed: {

    /**
     * Generates CSS style for element indent by nesting level.
     */
    nestingIndentStyleForm() {
      return 'padding-left: '+ (this.nestingLevel * 30) +'px;';
    },
    nestingIndentStyleMessage() {
      return 'padding-left: '+ (30 + this.nestingLevel * 30) +'px;';
    },

    /**
     * Indicates that all forms and groups are selected.
     */
    isAllItemsSelected() {
      let allGroupsSelected = true;
      let allFormsSelected = true;

      if (this.groups.length) allGroupsSelected = (this.selectedGroupKeys.length === this.notEmptySubgroupsCount);
      if (this.forms.length) allFormsSelected = (this.selectedFormIds.length === this.forms.length);

      return allGroupsSelected && allFormsSelected;
    },

    /**
     * Indicates that any content loading now (current group or any of subgroups).
     */
    isAnyContentLoading() {
      let isLoading = this.contentIsLoading;
      if (!isLoading) isLoading = this.subgroupsContentIsLoading;
      return isLoading;
    },
  },

  /**
   * Component initialization.
   */
  mounted: function () {
  },

  /**
   * Watched values.
   */
  watch : {

    /**
     * Watch the doLoadContent prop, which may be changed from parent FormGroup component.
     */
    doLoadContent: function(val) {
      if (!val) return;
      this.load();
    },

    /**
     * Watch the doDisplayContent prop, which may be changed from parent FormGroup component.
     */
    doDisplayContent: function(val) {
      this.contentDisplayed = val;
    },

    /**
     * Watch the doSelectContent prop, which may be changed from parent FormGroup component.
     */
    doSelectContent: function(val) {
      // Make sure content loaded.
      if (!this.contentLoaded) {
        this.load(() => { this.select(val); });
        return;
      }
      // If content loaded before, just select.
      this.select(val);
    },

    /**
     * Fire event to update parent.
     */
    contentLoaded: function (val) {
      this.$emit('onContentLoad', val);
    },

    /**
     * Fire event to update parent.
     */
    contentSelected: function (val) {
      this.$emit('onContentSelect', val);
    },

    /**
     * Fire event to update parent.
     */
    selectedFormIds: {
      deep: true,
      handler (val) {
        this.contentSelected = this.isAllItemsSelected;
        this.updateAllSelectedFormIds();
      }
    },

    /**
     * Fire event to update parent.
     */
    selectedGroupKeys: {
      deep: true,
      handler (val) {
        this.contentSelected = this.isAllItemsSelected;
        this.updateAllSelectedGroupKeys();
      }
    },

    /**
     * Fire event to update parent.
     */
    contentIsLoading: function(val) {
      this.$emit('onContentIsLoading', this.isAnyContentLoading);
    },

    /**
     * Fire event to update parent.
     */
    subgroupsContentIsLoading: function(val) {
      this.$emit('onContentIsLoading', this.isAnyContentLoading);
    },

    /**
     * Update isEmpty.
     */
    notEmptySubgroupsCount: function (val) {
      if ((val === 0) && (this.forms.length === 0)) {
        this.isEmpty = true;
        this.$emit('updateIsEmpty', this.isEmpty);
      }
    },

  },

}
</script>
